<template>
    <Teleport to="body">
        <div v-if="mostrar" class="modal-mask modal-entrega-apresentacao-mask" @click="fechar">
            <div
                class="jm margem modal-entrega-apresentacao"
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-entrega-titulo"
                @click.stop
            >
                <header class="modal-entrega-cabecalho">
                    <h2 id="modal-entrega-titulo">Escolher pacote de trabalho</h2>
                    <p v-if="rotuloProjeto" class="modal-entrega-subtitulo">{{ rotuloProjeto }}</p>
                    <p class="modal-entrega-dica">
                        Selecione um ou mais pacotes de trabalho para incluir na tabela. O mesmo projeto pode aparecer várias vezes.
                    </p>
                </header>

                <div v-if="carregando" class="modal-entrega-estado">
                    <span class="modal-entrega-spinner" aria-hidden="true"></span>
                    Carregando pacotes de trabalho…
                </div>

                <div v-else-if="!entregas.length" class="modal-entrega-estado">
                    Este projeto não possui pacotes de trabalho cadastrados.
                </div>

                <ul v-else class="modal-entrega-lista">
                    <li v-for="entrega in entregas" :key="entrega.id" class="modal-entrega-item">
                        <div class="modal-entrega-info">
                            <strong>{{ entrega.nome }}</strong>
                            <span class="modal-entrega-meta">
                                <template v-if="entrega.dtFim">Fim previsto: {{ formatarData(entrega.dtFim) }}</template>
                                <template v-if="entrega.status">
                                    <span v-if="entrega.dtFim"> · </span>{{ entrega.status }}
                                </template>
                            </span>
                        </div>
                        <button
                            type="button"
                            class="acao-secundaria modal-entrega-btn"
                            :class="{ 'modal-entrega-btn--adicionado': jaAdicionada(entrega.id) }"
                            :disabled="jaAdicionada(entrega.id)"
                            @click="$emit('adicionar', entrega.id)"
                        >
                            <i
                                class="bi"
                                :class="jaAdicionada(entrega.id) ? 'bi-check-lg' : 'bi-plus-lg'"
                                aria-hidden="true"
                            ></i>
                            {{ jaAdicionada(entrega.id) ? 'Adicionado' : 'Adicionar' }}
                        </button>
                    </li>
                </ul>

                <footer class="submit direita modal-entrega-acoes">
                    <button
                        v-if="permitirSemEntrega"
                        type="button"
                        class="acao-secundaria"
                        :disabled="projetoJaAdicionadoSemEntrega"
                        @click="$emit('adicionar-projeto')"
                    >
                        {{ projetoJaAdicionadoSemEntrega ? 'Projeto já adicionado' : 'Só o projeto (sem pacote de trabalho)' }}
                    </button>
                    <button type="button" @click="fechar">
                        Concluir
                    </button>
                </footer>
            </div>
        </div>
    </Teleport>
</template>

<script>
import { formatarDataBr } from '@/utils/date'

export default {
    name: 'ModalSelecionarEntregaApresentacao',

    props: {
        mostrar: { type: Boolean, default: false },
        pcmCodigo: { type: String, default: '' },
        nomeProjeto: { type: String, default: '' },
        entregas: { type: Array, default: () => [] },
        carregando: { type: Boolean, default: false },
        permitirSemEntrega: { type: Boolean, default: true },
        entregasAdicionadas: { type: Array, default: () => [] },
        projetoAdicionadoSemEntrega: { type: Boolean, default: false },
    },

    emits: ['fechar', 'adicionar', 'adicionar-projeto'],

    computed: {
        rotuloProjeto() {
            if (!this.nomeProjeto) return ''
            return this.pcmCodigo ? `${this.pcmCodigo} · ${this.nomeProjeto}` : this.nomeProjeto
        },

        projetoJaAdicionadoSemEntrega() {
            return this.projetoAdicionadoSemEntrega
        },
    },

    watch: {
        mostrar(aberto) {
            if (aberto) {
                document.addEventListener('keydown', this.onKeydown)
            } else {
                document.removeEventListener('keydown', this.onKeydown)
            }
        },
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.onKeydown)
    },

    methods: {
        formatarData(valor) {
            return formatarDataBr(String(valor).slice(0, 10))
        },

        jaAdicionada(entregaId) {
            const id = Number(entregaId)
            return this.entregasAdicionadas.some((eid) => Number(eid) === id)
        },

        fechar() {
            this.$emit('fechar')
        },

        onKeydown(event) {
            if (event.key === 'Escape') {
                event.preventDefault()
                this.fechar()
            }
        },
    },
}
</script>

<style scoped>
.modal-entrega-apresentacao-mask {
    z-index: 10050;
}

.modal-entrega-apresentacao {
    width: min(32rem, calc(100vw - 2rem));
    max-height: min(85vh, 40rem);
    display: flex;
    flex-direction: column;
    padding: 0;
    overflow: hidden;
}

.modal-entrega-cabecalho {
    padding: 1.1rem 1.25rem 0.75rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.modal-entrega-cabecalho h2 {
    margin: 0;
    font-size: 1.1rem;
}

.modal-entrega-subtitulo {
    margin: 0.35rem 0 0;
    font-size: 0.9rem;
    color: #444;
    line-height: 1.35;
}

.modal-entrega-dica {
    margin: 0.5rem 0 0;
    font-size: 0.8rem;
    color: #666;
    line-height: 1.4;
}

.modal-entrega-estado {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 2rem 1.25rem;
    font-size: 0.9rem;
    color: #555;
}

.modal-entrega-spinner {
    width: 1rem;
    height: 1rem;
    border: 2px solid rgba(0, 182, 179, 0.25);
    border-top-color: var(--cor-primaria, #00b6b3);
    border-radius: 50%;
    animation: modal-entrega-spin 0.7s linear infinite;
}

@keyframes modal-entrega-spin {
    to { transform: rotate(360deg); }
}

.modal-entrega-lista {
    list-style: none;
    margin: 0;
    padding: 0.5rem 0;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
}

.modal-entrega-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.65rem 1.25rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.modal-entrega-item:last-child {
    border-bottom: none;
}

.modal-entrega-info {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
}

.modal-entrega-info strong {
    font-size: 0.88rem;
    font-weight: 600;
    color: #222;
    line-height: 1.3;
}

.modal-entrega-meta {
    font-size: 0.78rem;
    color: #666;
}

.modal-entrega-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.35rem 0.65rem !important;
    font-size: 0.78rem !important;
    white-space: nowrap;
}

.modal-entrega-btn--adicionado {
    opacity: 0.7;
    cursor: default;
}

.modal-entrega-acoes {
    padding: 0.85rem 1.25rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    gap: 0.5rem;
    flex-wrap: wrap;
}
</style>
